import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Box } from "@mui/material";
import { CustomModal, CustomButton } from "@ippon/shared-components";
import {
  AREA_TYPE_LABELS,
  type AreaType,
  type RevenueType,
} from "@ippon/shared-types";
import type { AppDispatch } from "@/store";
import { createUnit } from "@/store/unitSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { getEmployeeID } from "@/utils/authUtils";
import UnitTabForm, {
  type UnitFormSubmitValues,
  type UnitFormSubmitHelpers,
} from "./UnitTabForm";
import { applyApiError } from "@/utils/applyApiError";
import { uploadPendingUnitDocuments } from "./sections/uploadUnitDocuments";
import { Can } from "@/components/auth/Can";

interface Props {
  open: boolean;
  propertyId: string;
  /** CardTab から開いたとき area_type を prefill + lock */
  defaultAreaType?: AreaType;
  /** 初期値で revenue_type を設定する (例: レントロール経由は "stock_rent" 既定) */
  defaultRevenueType?: RevenueType;
  /** モーダルタイトルを明示的に指定する (省略時は defaultAreaType から自動生成) */
  titleOverride?: string;
  onClose: () => void;
  /** 登録成功時に呼ばれる。breadcrumb / 詳細ページの初期表示用に landmark_name も渡す */
  onCreated?: (unitId: string, landmarkName?: string) => void;
}

const FORM_ID = "unit-create-form";

const UnitCreateModal: React.FC<Props> = ({
  open,
  propertyId,
  defaultAreaType,
  defaultRevenueType,
  titleOverride,
  onClose,
  onCreated,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // モーダルを開いた瞬間は必ず未入力状態として扱う (前回の dirty 状態が残らないように)
  useEffect(() => {
    if (open) setIsDirty(false);
  }, [open]);

  const handleSubmit = async (
    values: UnitFormSubmitValues,
    helpers: UnitFormSubmitHelpers,
  ) => {
    setSaving(true);
    try {
      // 添付書類は area_type=common/facility のとき *_detail.documents に内包されている。
      const rawDocs =
        values.area_type === "common"
          ? values.common_detail?.documents
          : values.area_type === "facility"
            ? values.facility_detail?.documents
            : undefined;
      let normalizedDocs = rawDocs;
      try {
        normalizedDocs = await uploadPendingUnitDocuments(rawDocs, propertyId);
      } catch {
        showToast({
          type: "error",
          message: "添付書類のアップロードに失敗しました",
        });
        return;
      }

      // アップロード後の documents を再度 *_detail に書き戻す
      const commonDetail =
        values.area_type === "common"
          ? { ...(values.common_detail ?? {}), documents: normalizedDocs }
          : values.common_detail;
      const facilityDetail =
        values.area_type === "facility"
          ? { ...(values.facility_detail ?? {}), documents: normalizedDocs }
          : values.facility_detail;

      const result = await dispatch(
        createUnit({
          property_id: propertyId,
          ...values,
          common_detail: commonDetail,
          facility_detail: facilityDetail,
        }),
      );
      if (createUnit.fulfilled.match(result)) {
        showToast({ type: "success", message: "登録しました" });
        onCreated?.(result.payload.unit_id, values.landmark_name);
        onClose();
      } else {
        // API のフィールド単位エラー (名称重複等) は該当 input 下にインライン表示。
        // それ以外 (500/通信失敗) はトースト。BE/FE とも field 名は landmark_name で一致。
        applyApiError(result.payload, {
          setError: helpers.setError,
          setFocus: helpers.setFocus,
          onFallback: (p) =>
            showToast({
              type: "error",
              message: p.message || "登録に失敗しました",
            }),
        });
      }
    } finally {
      setSaving(false);
    }
  };

  /**
   * モーダルフッターの「登録」ボタンから body 内の <form> を送信する。
   * CustomButton は form 属性をパススルーしないので、ref + requestSubmit() で発火。
   */
  const triggerSubmit = () => {
    const form = bodyRef.current?.querySelector(
      `form#${FORM_ID}`,
    ) as HTMLFormElement | null;
    form?.requestSubmit();
  };

  const title =
    titleOverride ??
    (defaultAreaType
      ? `${AREA_TYPE_LABELS[defaultAreaType]}登録`
      : "区画登録");

  return (
    <CustomModal
      openModal={open}
      handleCloseModal={onClose}
      title={title}
      modalType="registerModal"
      addTitleBorder
      confirmOnDirtyClose={isDirty}
    >
      {open && (
        <>
          <Box className="registerModalBody" ref={bodyRef}>
            <Box sx={{ maxWidth: "600px" }}>
              <UnitTabForm
                propertyId={propertyId}
                initialValues={(() => {
                  // 新規登録時は管理責任者をログイン中のユーザーで初期化する
                  // (区画詳細編集側ではあえて空のままにできるよう、ここだけで補完)
                  const currentEmployeeId = getEmployeeID();
                  return {
                    ...(defaultAreaType
                      ? { area_type: defaultAreaType }
                      : {}),
                    ...(defaultRevenueType
                      ? { revenue_type: defaultRevenueType }
                      : {}),
                    ...(currentEmployeeId
                      ? { manager_employee_id: String(currentEmployeeId) }
                      : {}),
                  };
                })()}
                onSubmit={handleSubmit}
                hideActionBar
                formId={FORM_ID}
                onDirtyChange={setIsDirty}
              />
            </Box>
          </Box>
          <Box className="registerModalFooter">
            <CustomButton
              label="戻る"
              buttonCategory="cancelModal"
              onClick={onClose}
              disabled={saving}
              sx={{ minWidth: "140px !important", width: "140px" }}
            />
            <Can action="unit:edit">
              <CustomButton
                label="新規登録"
                buttonCategory="submit"
                onClick={triggerSubmit}
                isLoading={saving}
                disabled={saving || !isDirty}
                sx={{ minWidth: "140px !important", width: "140px" }}
              />
            </Can>
          </Box>
        </>
      )}
    </CustomModal>
  );
};

export default UnitCreateModal;
